// components/pages/RecentsPage.js
import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Spinner, Button } from 'react-bootstrap';

import IntegratedSearchNavBar from '../navigation/IntegratedSearchNavBar';
import { usePageSearch } from '../navigation/SearchContext';
import { useRecentsData } from '../../hooks/useRecentsData';
import FeedItem from '../feed/FeedItem';

const RecentsPage = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Extract schema from path
  const extractSchemaFromPath = (pathname) => {
    const match = pathname.match(/^\/([^\/]+)\/recents/);
    return match ? match[1] : null;
  };

  const schema =
    params.schema ||
    params.country ||
    params.countryCode ||
    extractSchemaFromPath(location.pathname) ||
    'us';

  // Convert schema to proper format for FeedItem
  const getSchemaForFeedItem = (countryCode) => {
    const mapping = {
      us: 'united_states_of_america',
      canada: 'canada',
      mexico: 'mexico',
      uk: 'united_kingdom',
    };
    return mapping[countryCode] || countryCode;
  };

  const [feedLength, setFeedLength] = useState(30);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchPerformed, setSearchPerformed] = useState(false);

  // URL is the single source of truth. The integrated search bar commits a
  // query to the URL; this effect derives all search state from it (including
  // on first load / direct navigation / back-forward).
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const query = searchParams.get('query'); // already decoded by URLSearchParams

    if (query) {
      setSearchQuery(query);
      setSearchPerformed(true);
    } else {
      setSearchQuery('');
      setSearchPerformed(false);
    }

    // New search (or cleared search) always resets pagination.
    setFeedLength(30);
  }, [location.search]);

  // Commit a search by writing it to the URL; the effect above does the rest.
  const submitSearch = useCallback(
    (rawQuery) => {
      const trimmed = (rawQuery ?? '').trim();
      const searchParams = new URLSearchParams();
      if (trimmed) {
        searchParams.set('query', trimmed); // URLSearchParams handles encoding
      }
      const queryString = searchParams.toString();
      navigate(queryString ? `${location.pathname}?${queryString}` : location.pathname, { replace: true });
    },
    [location.pathname, navigate]
  );

  // Take over the global integrated search bar while this page is mounted.
  // Registering an onSearch handler makes the bottom search bar search IN-PAGE
  // (recents) instead of navigating to /search. On unmount it resets to the
  // default navigate-to-/search behavior.
  usePageSearch({
    placeholder: 'Search recents',
    onSearch: submitSearch,
    value: searchQuery,
  });

  // Fetch recents data
  const { data, loading, error } = useRecentsData(
    schema,
    feedLength,
    searchQuery,
    searchPerformed
  );

  // Handle load more
  const handleLoadMore = useCallback(() => {
    setFeedLength((prev) => prev + 20);
  }, []);

  // Transform item data for FeedItem component
  const transformItemForFeedItem = useCallback((item) => {
    return {
      ...item,
      title: item.title || item.status_title,
      url: item.url,
      document_link: item.document_link || item.download?.pdfLink,
      created_at: item.created_at,
    };
  }, []);

  const feedSchema = getSchemaForFeedItem(schema);

  const renderBody = () => {
    if (loading && (!data || data.length === 0)) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '200px' }}
        >
          <Spinner id="spinner" animation="grow" variant="light" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center text-light p-5">
          <p>Error fetching data. Please try again.</p>
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className="text-center p-5" style={{ color: 'var(--text-tertiary)' }}>
          {searchPerformed && searchQuery ? (
            <p>No results found for "{searchQuery}"</p>
          ) : (
            <p>No recent content available.</p>
          )}
        </div>
      );
    }

    return (
      <>
        {searchPerformed && searchQuery && (
          <div className="text-center text-light py-3">
            Found {data.length} results for "{searchQuery}"
          </div>
        )}

        {data.map((item, index) => {
          const transformedItem = transformItemForFeedItem(item);
          const sourceTable = item.source || 'unknown';

          return (
            <div key={`${item.id || index}-recents`}>
              <FeedItem
                item={transformedItem}
                feedName={item.collection_name || sourceTable}
                branch=""
                topic=""
                buttonName="Document"
                schema={feedSchema}
                table={sourceTable}
                expanded={false}
                isRecentsItem={true}
              />
            </div>
          );
        })}

        {/* Load more button */}
        <div className="d-flex justify-content-center align-items-center py-4">
          <Button id="feedViewMoreButton" onClick={handleLoadMore}>
            {loading ? (
              <Spinner animation="border" size="sm" variant="light" />
            ) : (
              'More'
            )}
          </Button>
        </div>

        {/* Bottom spacing */}
        <div style={{ height: '100px' }}></div>
      </>
    );
  };

  return (
    <>
      <IntegratedSearchNavBar currentCountry={schema} />
      <div id="homebg">
        <div className="recents-page-content">{renderBody()}</div>
      </div>

      <style>{`
        /* Match the Glance column width on the homepage so recents cards read
           as cards rather than full-width bars on wide desktop screens. */
        .recents-page-content {
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          padding-left: 10px;
          padding-right: 10px;
          box-sizing: border-box;
        }
      `}</style>
    </>
  );
};

export default RecentsPage;